import { Seo } from "../components/Seo";
import { PageHero } from "../components/PageHero";
import { Reveal } from "../components/Reveal";
import { SectionHeading } from "../components/SectionHeading";
import { CtaBanner } from "../components/CtaBanner";
import { Placeholder, isPlaceholder } from "../components/Placeholder";
import { site, programs } from "../config/siteConfig";

const academicSections = [
  {
    title: "Classes & Grades",
    body: site.classes,
    note: "The school office can confirm which classes are currently open for admission.",
  },
  {
    title: "Curriculum",
    body: site.curriculum,
    note: "Curriculum details will be published once confirmed by the school.",
  },
  {
    title: "Board / Affiliation",
    body: site.board,
    note: "No board affiliation is claimed until it is verified.",
  },
  {
    title: "Subjects",
    body: site.subjects,
    note: "A verified subject list will be published here.",
  },
  {
    title: "Teaching Approach",
    body: "Teachers focus on clear concepts, regular practice and individual attention, so every student can keep up and grow in confidence.",
  },
  {
    title: "Learning Activities",
    body: "Classroom learning is supported by activities that encourage participation, curiosity and teamwork. Verified activity details will be added once confirmed.",
  },
  {
    title: "Assessment",
    body: "[INSERT VERIFIED ASSESSMENT APPROACH]",
    note: "Ask the school office how student progress is assessed and shared with parents.",
  },
  {
    title: "Student Development",
    body: "Alongside academics, students are encouraged to build discipline, respect and good character — the habits that matter beyond the classroom.",
  },
];

export default function AcademicsPage() {
  return (
    <>
      <Seo
        title="Academics | Ansar Public School Mundeke Goraya, Daska"
        description="Explore academics at Ansar Public School Mundeke — classes, curriculum, subjects and teaching approach. Verified details published as confirmed."
      />
      <PageHero
        chapter="01"
        eyebrow="Academics"
        title="Learning, Step by Step"
        description="An honest overview of academics at Ansar Public School. Details that are still being verified are clearly marked as editable placeholders."
        crumb="Academics"
      />

      <section className="border-b border-brand-line bg-white">
        <div className="mx-auto max-w-7xl px-4 py-16 sm:px-6 lg:py-20">
          <Reveal>
            <SectionHeading
              chapter="02"
              eyebrow="Programs"
              title="Academic Programs"
              description="Program cards below are placeholders until the school confirms which grades are offered."
            />
          </Reveal>
          <div className="mt-10 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
            {programs.map((p, i) => (
              <Reveal key={p.id} delay={i * 0.08} className="h-full">
                <article
                  data-testid={`academics-program-${p.id}`}
                  className="flex h-full flex-col gap-3 rounded-3xl border border-brand-line bg-brand-cream p-6"
                >
                  <span className="font-display text-sm font-extrabold text-brand-amber">
                    {String(i + 1).padStart(2, "0")}
                  </span>
                  <h3 className="font-display text-xl font-bold text-brand-ink">{p.title}</h3>
                  <Placeholder>{p.text}</Placeholder>
                </article>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-5xl px-4 py-16 sm:px-6 lg:py-24">
        <Reveal>
          <SectionHeading
            chapter="03"
            eyebrow="Structure"
            title="The Academic Framework"
            align="center"
          />
        </Reveal>
        <div className="mt-12 space-y-4">
          {academicSections.map((s, i) => (
            <Reveal key={s.title} delay={Math.min(i * 0.05, 0.3)}>
              <article
                data-testid={`academic-section-${i}`}
                className="grid gap-4 rounded-3xl border border-brand-line bg-white p-6 shadow-sm transition-shadow hover:shadow-md sm:grid-cols-[72px_1fr] sm:p-8"
              >
                <span className="font-display text-3xl font-extrabold text-brand-light [text-shadow:0_0_1px_#164E63] sm:text-4xl" aria-hidden="true">
                  {String(i + 1).padStart(2, "0")}
                </span>
                <div>
                  <h2 className="font-display text-xl font-bold text-brand-ink">{s.title}</h2>
                  <div className="mt-2 text-base leading-relaxed text-brand-muted">
                    {isPlaceholder(s.body) ? <Placeholder>{s.body}</Placeholder> : <p>{s.body}</p>}
                  </div>
                  {s.note && <p className="mt-2 text-sm text-brand-muted/80">{s.note}</p>}
                </div>
              </article>
            </Reveal>
          ))}
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-4 pb-16 sm:px-6 lg:pb-24">
        <CtaBanner
          idPrefix="academics-admissions"
          title="Have Questions About Classes?"
          text="Call or message the school to confirm available classes, subjects and curriculum details before applying."
          primaryLabel="Start an Admission Enquiry"
          primaryTo="/admissions"
          secondaryLabel="Call the School"
          secondaryHref={site.phoneHref}
        />
      </section>
    </>
  );
}
